import { useMemo } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { WorkspacePulseEvent } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UsePulseEventsModuleParams {
  pulseEvents: WorkspacePulseEvent[]
  timeRange: AnalysisTimeRangeKey
  loading?: boolean
  error?: Error | null
  now?: Date
}

export interface UsePulseEventsModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export const usePulseEventsModule = ({
  pulseEvents,
  timeRange,
  loading = false,
  error = null,
  now = new Date(),
}: UsePulseEventsModuleParams): UsePulseEventsModuleResult => {
  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    const inWindow = pulseEvents.filter((e) => {
      const d = parseDate(e.createdAt)
      return d && d >= from && d <= to
    })

    const totalCount = inWindow.length

    const dates = inWindow
      .map((e) => parseDate(e.createdAt))
      .filter((d): d is Date => d !== null)

    const points = bucketize(dates, timeRange, now)
    const delta = calculateDelta(points)

    const topRecords: LiveModuleRecord[] = [...inWindow]
      .sort((a, b) => {
        const da = parseDate(a.createdAt)?.getTime() ?? 0
        const db = parseDate(b.createdAt)?.getTime() ?? 0
        return db - da
      })
      .slice(0, 5)
      .map((e, index) => {
        const title = e.title?.trim() || 'Pulse event'
        const severity = e.severity || 'INFO'
        const type = e.type ? e.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Event'
        return {
          id: e.id || `pulse-event-${index}`,
          primary: title,
          secondary: `${severity} · ${type}`,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = totalCount === 0
      ? { value: '—', label: 'no records' }
      : { value: totalCount, delta, label: 'events' }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records: topRecords,
      loading,
      error,
    }
  }, [pulseEvents, timeRange, loading, error, now])
}
